import React, { useState } from 'react';
import { Key, Plus } from 'lucide-react';
import { useI18n } from '../../utils/i18n';
import apiService from '../../services/apiService';
import { useConfirm } from '../ui/ConfirmDialog';
import { useToast } from '../ToastProvider';

// #312: Reiter „API-Keys“ des Admin-Bereichs, Code unverändert aus
// AdminPanel.js übernommen (Muster und Begründung: admin/LogsTab.js).
//
// Hier zählt die Lebensdauer des Zustands besonders: `createdApiKey` ist der
// Klartext-Key, der genau einmal angezeigt wird. Er übersteht wie bisher den
// Wechsel zu einem anderen Reiter und zurück. Die Benutzerliste für die
// Zuordnung kommt vom Panel, weil auch der Benutzer-Reiter sie braucht.
export function useApiKeysTab({ setError }) {
  const { t } = useI18n();
  const confirm = useConfirm();
  const { pushToast } = useToast();
  // API-Keys State (#12)
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [newApiKey, setNewApiKey] = useState({ name: '', lifetimeDays: '30', userId: '' });
  const [creatingApiKey, setCreatingApiKey] = useState(false);
  // Klartext-Key aus der Create-Antwort — wird genau einmal angezeigt
  const [createdApiKey, setCreatedApiKey] = useState(null);

  // ===== API-Keys (#12) =====
  const loadApiKeys = async () => {
    setApiKeysLoading(true);
    try {
      const resp = await apiService.getApiKeys();
      setApiKeys(resp.apiKeys || []);
    } catch (e) {
      console.error('Error loading api keys:', e);
      setError(t('admin.apiKeys.loadError', 'Fehler beim Laden der API-Keys.'));
    } finally {
      setApiKeysLoading(false);
    }
  };

  const handleCreateApiKey = async (e) => {
    e.preventDefault();
    if (!newApiKey.name.trim()) return;
    setCreatingApiKey(true);
    setError(null);
    try {
      const resp = await apiService.createApiKey({
        name: newApiKey.name.trim(),
        lifetimeDays: Number(newApiKey.lifetimeDays),
        userId: newApiKey.userId || null
      });
      setCreatedApiKey({ key: resp.key, name: resp.apiKey?.name });
      setNewApiKey({ name: '', lifetimeDays: '30', userId: '' });
      await loadApiKeys();
    } catch (error) {
      console.error('Error creating api key:', error);
      setError(error.response?.data?.error || t('admin.apiKeys.createError', 'Fehler beim Erzeugen des API-Keys.'));
    } finally {
      setCreatingApiKey(false);
    }
  };

  const handleRevokeApiKey = async (key) => {
    if (!(await confirm({ message: t('admin.apiKeys.revokeConfirm', 'Diesen API-Key wirklich widerrufen? Anwendungen, die ihn benutzen, verlieren sofort den Zugriff.'), danger: true }))) {
      return;
    }
    setError(null);
    try {
      await apiService.revokeApiKey(key.id);
      pushToast({ message: t('admin.apiKeys.revoked', 'API-Key widerrufen.'), type: 'success' });
      await loadApiKeys();
    } catch (error) {
      console.error('Error revoking api key:', error);
      setError(error.response?.data?.error || t('admin.apiKeys.revokeError', 'Fehler beim Widerrufen des API-Keys.'));
    }
  };

  return { apiKeys, apiKeysLoading, newApiKey, setNewApiKey, creatingApiKey, createdApiKey, setCreatedApiKey, loadApiKeys, handleCreateApiKey, handleRevokeApiKey };
}

const ApiKeysTab = ({ tab, users }) => {
  const { getLocale, t } = useI18n();
  const { apiKeys, apiKeysLoading, newApiKey, setNewApiKey, creatingApiKey, createdApiKey, setCreatedApiKey, handleCreateApiKey, handleRevokeApiKey } = tab;

  return (
    <div className="admin-section">
      <h3>{t('admin.tabs.apiKeys', 'API-Keys')}</h3>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: -6 }}>
        {t('admin.apiKeys.intro', 'Bearer-Keys für Maschinen-Zugriffe (KI-Agenten, Integrationen). Der Key agiert mit den Rechten des zugeordneten Benutzers und wird nur einmal angezeigt.')}
      </p>
      {/* #202: Reichweite + Rotationsregel sichtbar im Dialog, nicht nur in der Doku */}
      <div className="alert alert-warning" style={{ fontSize: 13 }}>
        {t('admin.apiKeys.reachNotice', 'Ein Key liest alles, was der zugeordnete Benutzer lesen darf — auch ticket-verknüpfte E-Mail- und Teams-Inhalte. Laufzeit ist Pflicht: Abgelaufene Keys werden nicht verlängert, sondern neu ausgestellt (Rotation).')}
      </div>

      {createdApiKey && (
        <div className="alert alert-success" style={{ wordBreak: 'break-all' }}>
          <strong>{t('admin.apiKeys.createdFor', 'Neuer Key für')} „{createdApiKey.name}":</strong>
          <div style={{ fontFamily: 'monospace', margin: '6px 0' }}>{createdApiKey.key}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { try { navigator.clipboard.writeText(createdApiKey.key); } catch (_) {} }}>
              {t('admin.apiKeys.copy', 'Kopieren')}
            </button>
            <span style={{ fontSize: 12 }}>{t('admin.apiKeys.showOnce', 'Der Key wird nur dieses eine Mal angezeigt — jetzt sicher ablegen!')}</span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCreatedApiKey(null)}>×</button>
          </div>
        </div>
      )}

      <form onSubmit={handleCreateApiKey} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
        <div className="form-group" style={{ minWidth: 220 }}>
          <label>{t('admin.apiKeys.name', 'Name')} *</label>
          <input
            type="text"
            className="form-input"
            value={newApiKey.name}
            onChange={(e) => setNewApiKey(prev => ({ ...prev, name: e.target.value }))}
            placeholder={t('admin.apiKeys.namePlaceholder', 'z. B. Placetel-Agent')}
            disabled={creatingApiKey}
            required
          />
        </div>
        <div className="form-group">
          {/* #202: Laufzeit ist Pflicht — „Unbefristet" gibt es nicht mehr */}
          <label>{t('admin.apiKeys.lifetime', 'Laufzeit')} *</label>
          <select
            className="form-input"
            value={newApiKey.lifetimeDays}
            onChange={(e) => setNewApiKey(prev => ({ ...prev, lifetimeDays: e.target.value }))}
            disabled={creatingApiKey}
          >
            {['1', '7', '30', '90', '180', '365'].map(d => (
              <option key={d} value={d}>{d} {t('admin.apiKeys.days', 'Tage')}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>{t('admin.apiKeys.user', 'Agiert als Benutzer')}</label>
          <select
            className="form-input"
            value={newApiKey.userId}
            onChange={(e) => setNewApiKey(prev => ({ ...prev, userId: e.target.value }))}
            disabled={creatingApiKey}
          >
            <option value="">{t('admin.apiKeys.userSelf', 'Ich selbst')}</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.displayName || u.email}</option>
            ))}
          </select>
        </div>
        <button type="submit" className="btn btn-primary" disabled={creatingApiKey || !newApiKey.name.trim()}>
          <Plus size={16} />
          {creatingApiKey ? t('admin.apiKeys.creating', 'Erzeuge…') : t('admin.apiKeys.create', 'Key erzeugen')}
        </button>
      </form>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t('admin.apiKeys.name', 'Name')}</th>
              <th>{t('admin.apiKeys.prefix', 'Key')}</th>
              <th>{t('admin.apiKeys.user', 'Agiert als Benutzer')}</th>
              <th>{t('admin.apiKeys.expires', 'Läuft ab')}</th>
              <th>{t('admin.apiKeys.lastUsed', 'Zuletzt benutzt')}</th>
              <th>{t('admin.apiKeys.status', 'Status')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {apiKeys.map(k => (
              <tr key={k.id} style={k.status !== 'active' ? { opacity: 0.55 } : undefined}>
                <td>{k.name}</td>
                <td style={{ fontFamily: 'monospace' }}>{k.keyPrefix}…</td>
                <td>{k.userDisplayName || k.userEmail || `#${k.userId}`}</td>
                <td>{k.expiresAt ? new Date(k.expiresAt).toLocaleDateString(getLocale()) : '—'}</td>
                <td>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString(getLocale()) : t('common.never', 'Nie')}</td>
                <td>
                  {k.status === 'active' && <span style={{ color: 'var(--color-success)' }}>{t('admin.apiKeys.statusActive', 'aktiv')}</span>}
                  {k.status === 'expired' && <span style={{ color: 'var(--color-orange)' }}>{t('admin.apiKeys.statusExpired', 'abgelaufen')}</span>}
                  {k.status === 'revoked' && (
                    <span style={{ color: 'var(--color-danger)' }} title={k.revokedBy ? `${t('admin.apiKeys.revokedBy', 'Widerrufen von')} ${k.revokedBy}` : undefined}>
                      {t('admin.apiKeys.statusRevoked', 'widerrufen')}
                    </span>
                  )}
                </td>
                <td>
                  {k.status !== 'revoked' && (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleRevokeApiKey(k)}>
                      {t('admin.apiKeys.revoke', 'Widerrufen')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {apiKeys.length === 0 && !apiKeysLoading && (
        <div className="empty-state">
          <Key size={48} style={{ color: 'var(--color-text-muted)', marginBottom: '16px' }} />
          <p>{t('admin.apiKeys.none', 'Noch keine API-Keys angelegt.')}</p>
        </div>
      )}
    </div>
  );
};

export default ApiKeysTab;
